/**
 * shareInvitationService.js — Appels API des invitations de partage cheval.
 *
 * Source unique des appels /api/shares/pending et /api/shares/:token/accept,
 * consommée par le bandeau PendingInvitations (les trois shells : propriétaire,
 * Mode Pro Vétérinaire, OwnerAsVetShell) et par useAuthState pour le jeton du
 * lien d'invitation (?share=token).
 *
 * Le jeton du lien e-mail est mis de côté en localStorage tant que
 * l'utilisateur n'est pas authentifié, puis consommé à la connexion comme à la
 * restauration de session (correctif 10.5.294 : avant, seule la connexion le
 * consommait, un clic avec une session déjà ouverte ne faisait rien).
 */
import { getAuthHeaders } from '../utils/authFetch';

export const PENDING_SHARE_STORAGE_KEY = 'ekynair_pending_share';

// Statut HTTP de /accept → statut lisible (clé i18n share.invitation.result.*).
// 404 couvre « déjà acceptée » : le contrôleur ne cherche que status 'pending'.
const ACCEPT_STATUS_BY_HTTP = { 403: 'wrongAccount', 404: 'notFound', 410: 'expired' };

const requestInit = (method) => ({
  method,
  credentials: 'include',
  headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
});

/**
 * GET /api/shares/pending
 * @returns {Promise<{ ok: boolean, invitations: Array }>}
 */
export async function fetchPendingInvitations() {
  try {
    const res = await fetch('/api/shares/pending', requestInit('GET'));
    const data = await res.json();
    if (data?.success) return { ok: true, invitations: data.invitations || [] };
    return { ok: false, invitations: [] };
  } catch (err) {
    console.error('fetchPendingInvitations:', err);
    return { ok: false, invitations: [] };
  }
}

/**
 * POST /api/shares/:token/accept
 * @returns {Promise<{ status: 'accepted'|'wrongAccount'|'notFound'|'expired'|'error' }>}
 */
export async function acceptShareInvitation(token) {
  try {
    const res = await fetch(`/api/shares/${encodeURIComponent(token)}/accept`, requestInit('POST'));
    if (ACCEPT_STATUS_BY_HTTP[res.status]) return { status: ACCEPT_STATUS_BY_HTTP[res.status] };
    const data = await res.json();
    return { status: data?.success ? 'accepted' : 'error' };
  } catch (err) {
    console.error('acceptShareInvitation:', err);
    return { status: 'error' };
  }
}

/** Met de côté le jeton du lien d'invitation jusqu'à l'authentification. */
export function storePendingShareToken(token) {
  localStorage.setItem(PENDING_SHARE_STORAGE_KEY, token);
}

/** Vrai si un jeton de lien d'invitation attend d'être consommé. */
export function hasPendingShareToken() {
  return Boolean(localStorage.getItem(PENDING_SHARE_STORAGE_KEY));
}

/**
 * Consomme le jeton mis de côté : lecture et retrait SYNCHRONES avant l'appel,
 * pour qu'une connexion et une restauration de session qui se chevauchent ne
 * déclenchent qu'un seul /accept (le second appelant trouve le stockage vide).
 * @returns {Promise<null | { status: string }>} null si aucun jeton.
 */
export async function consumePendingShareToken() {
  const token = localStorage.getItem(PENDING_SHARE_STORAGE_KEY);
  if (!token) return null;
  localStorage.removeItem(PENDING_SHARE_STORAGE_KEY);
  return acceptShareInvitation(token);
}
